// client/src/variables.js
//
// Single place where the app learns about its environment. Everything else
// (axios calls, SEO metadata, Google sign-in) imports from here.
//
// Where the values come from (Next.js picks the file by command):
//   next dev            .env.development  -> http://localhost:3010 (API), http://localhost:3000 (site)
//   next build / start  .env.production   -> https://vhserver.desync.in (API), https://vidyalayhelp.com (site)
//   .env.local          your machine only; overrides either of the above
//   Vercel dashboard    overrides the files for deployed builds
//
// If a variable is missing, the fallbacks below choose by hostname, so a forgotten
// setting never makes a production site talk to localhost (or a dev box to production).

import { PRODUCTION_SITE_URL } from '@/lib/domains';

const PRODUCTION_API_URL = 'https://vhserver.desync.in';
const DEVELOPMENT_API_URL = 'http://localhost:3010';
const DEVELOPMENT_SITE_URL = 'http://localhost:3000';

const clean = (url) => url.trim().replace(/\/+$/, '');

const isLocalHost = (h) => h === 'localhost' || h === '127.0.0.1' || h === '[::1]';
// Testing from a phone on the same Wi-Fi: the API runs on the dev machine, same LAN IP.
const isPrivateIp = (h) => /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(h);

export const IS_PRODUCTION = process.env.NODE_ENV === 'production';

// Hosts under these suffixes are separate sites per tenant (public suffix list), so
// a.vercel.app and b.vercel.app are cross-site even though they share two labels.
const SHARED_SUFFIXES = ['vercel.app'];

/** "app.example.com" -> "example.com"; "x.vercel.app" -> "x.vercel.app". */
const siteOf = (host) => {
  const h = host.toLowerCase();
  const suffix = SHARED_SUFFIXES.find((s) => h.endsWith(`.${s}`));
  const labels = h.split('.');
  return labels.slice(suffix ? -(suffix.split('.').length + 1) : -2).join('.');
};

/**
 * Path on the web app's own origin that next.config.mjs rewrites to the API.
 *
 * Login is an HTTP-only cookie set by the API. When the API lives on a different site
 * from the page (vidyalayhelp.com -> *.vercel.app) that cookie is a third-party cookie,
 * which Safari, Brave, Firefox and Chrome incognito refuse to store: login "succeeds",
 * then /dashboard finds no session and sends the user back to /login. Calling the API
 * through the page's own origin makes the cookie first-party.
 */
export const API_PROXY_PATH = '/backend';

const getApiUrl = () => {
  const configured = process.env.NEXT_PUBLIC_API_URL ? clean(process.env.NEXT_PUBLIC_API_URL) : '';

  if (configured && typeof window !== 'undefined') {
    const { hostname } = window.location;
    try {
      const apiHost = new URL(configured).hostname;
      const local = isLocalHost(hostname) || isPrivateIp(hostname);
      if (!local && siteOf(apiHost) !== siteOf(hostname)) return API_PROXY_PATH;
    } catch {
      // Not an absolute URL (already a path) - use it as given.
    }
  }

  if (configured) return configured;

  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (isLocalHost(hostname)) return DEVELOPMENT_API_URL;
    if (isPrivateIp(hostname)) return `http://${hostname}:3010`;
    return PRODUCTION_API_URL;
  }

  return IS_PRODUCTION ? PRODUCTION_API_URL : DEVELOPMENT_API_URL;
};

const getSiteUrl = () => {
  if (process.env.NEXT_PUBLIC_SITE_URL) return clean(process.env.NEXT_PUBLIC_SITE_URL);
  return IS_PRODUCTION ? PRODUCTION_SITE_URL : DEVELOPMENT_SITE_URL;
};

/** Backend API base URL. */
export const API_URL = getApiUrl();

/**
 * Absolute backend URL, for showing to people who will call the public API from
 * their own sites. API_URL can be the relative '/backend' proxy path, which is
 * meaningless outside this web app.
 */
export const PUBLIC_API_URL = (() => {
  if (API_URL.startsWith('http')) return API_URL;
  const configured = process.env.NEXT_PUBLIC_API_URL ? clean(process.env.NEXT_PUBLIC_API_URL) : '';
  return configured || (IS_PRODUCTION ? PRODUCTION_API_URL : DEVELOPMENT_API_URL);
})();

/** Public URL of this web app (SEO metadata, sitemap, canonical links). */
export const SITE_URL = getSiteUrl();

/**
 * Google OAuth web client ID (public by design - it ships to the browser).
 * Created in the `vidyalayhelp` GCP project; empty hides the Google button.
 */
export const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || '';

// Debug logging (only in development)
if (!IS_PRODUCTION && typeof window !== 'undefined') {
  console.log('🔧 Environment:', {
    mode: process.env.NODE_ENV,
    API_URL,
    SITE_URL,
    googleSignIn: Boolean(GOOGLE_CLIENT_ID),
    hostname: window.location.hostname,
  });
}
